<template>
  <div class="automations-view scroll-container">
    <div class="container">
      <PageHeader
        :title="$t('automations_page.title')"
        :description="$t('automations_page.description')"
      >
        <template #actions>
          <Btn variant="secondary" icon="folder-plus" @click="addGroup">
            {{ $t('automations_page.add_group') }}
          </Btn>
          <Btn variant="primary" @click="createNewAutomation()" :loading="isCreating">
            {{ $t('automations_page.add') }}
          </Btn>
        </template>
      </PageHeader>

      <EmptyState
        v-if="!automations.length && !groups.length"
        :title="$t('automations_page.empty_title')"
        :description="$t('automations_page.empty_description')"
        icon="duotone-folders"
      >
        <Btn
          :text="$t('automations_page.add')"
          icon="plus"
          variant="secondary"
          @click="createNewAutomation()"
        />
      </EmptyState>

      <div v-else class="automations-board">
        <div class="automations-board__header">
          <span class="automations-board__col automations-board__col--grip"></span>
          <span class="automations-board__col automations-board__col--name">
            {{ $t('automations_page.columns.name') }}
          </span>
          <span class="automations-board__col automations-board__col--num">
            {{ $t('automations_page.columns.updated') }}
          </span>
          <span class="automations-board__col automations-board__col--num">
            {{ $t('automations_page.columns.runs') }}
          </span>
          <span class="automations-board__col automations-board__col--num">
            {{ $t('automations_page.columns.succeeded') }}
          </span>
          <span class="automations-board__col automations-board__col--num">
            {{ $t('automations_page.columns.failed') }}
          </span>
          <span class="automations-board__col automations-board__col--status">
            {{ $t('automations_page.columns.status') }}
          </span>
          <span class="automations-board__col automations-board__col--actions"></span>
        </div>

        <draggable
          v-model="boardGroups"
          class="automations-board__groups"
          item-key="id"
          handle=".group-drag-handle"
          ghost-class="automation-group--ghost"
          @end="persistOrder"
        >
          <template #item="{ element: group }">
            <section class="automation-group">
              <header class="automation-group__header">
                <button class="group-drag-handle" type="button" aria-label="Reorder group">
                  <BaseIcon name="grip-dots" size="16" />
                </button>
                <button
                  class="automation-group__toggle"
                  type="button"
                  @click="toggleGroup(group.id)"
                >
                  <BaseIcon
                    name="chevron"
                    size="14"
                    :class="{ 'is-collapsed': !groupExpanded[group.id] }"
                  />
                  <h3>{{ group.name }}</h3>
                  <span class="automation-group__count">{{ group.items.length }}</span>
                </button>
                <div class="automation-group__actions">
                  <Btn
                    variant="plain"
                    size="sm"
                    icon="edit"
                    type="button"
                    @click="editGroup(group)"
                  />
                  <Btn
                    variant="plain"
                    size="sm"
                    icon="plus"
                    type="button"
                    @click="createNewAutomation(group.id)"
                  />
                  <Btn
                    variant="plain"
                    size="sm"
                    icon="trash"
                    type="button"
                    @click="confirmDeleteGroup(group)"
                  />
                </div>
              </header>

              <div v-show="groupExpanded[group.id] !== false" class="automation-group__body">
                <draggable
                  :list="group.items"
                  class="automation-group__list"
                  :class="{ 'automation-group__list--empty': !group.items.length }"
                  group="automation-rows"
                  item-key="id"
                  handle=".row-drag-handle"
                  ghost-class="automation-row--ghost"
                  @end="persistOrder"
                >
                  <template #item="{ element: automation }">
                    <AutomationRow
                      :automation="automation"
                      @open="handleOpenAutomation"
                      @toggle="handleToggleAutomation"
                      @delete="handleDeleteAutomation"
                    />
                  </template>
                </draggable>
                <button
                  v-if="!group.items.length"
                  class="automation-group__empty"
                  type="button"
                  @click="createNewAutomation(group.id)"
                >
                  <BaseIcon name="plus" size="14" />
                  {{ $t('automations_page.new_in_group') }}
                </button>
              </div>
            </section>
          </template>
        </draggable>

        <section class="automation-group automation-group--ungrouped">
          <header class="automation-group__header">
            <div class="automation-group__toggle automation-group__toggle--static">
              <BaseIcon name="cubes" size="14" />
              <h3>{{ $t('automations_page.ungrouped') }}</h3>
              <span class="automation-group__count">{{ ungroupedItems.length }}</span>
            </div>
            <div class="automation-group__actions">
              <Btn
                variant="plain"
                size="sm"
                icon="plus"
                type="button"
                @click="createNewAutomation(null)"
              />
            </div>
          </header>
          <div class="automation-group__body">
            <draggable
              v-model="ungroupedItems"
              class="automation-group__list"
              :class="{ 'automation-group__list--empty': !ungroupedItems.length }"
              group="automation-rows"
              item-key="id"
              handle=".row-drag-handle"
              ghost-class="automation-row--ghost"
              @end="persistOrder"
            >
              <template #item="{ element: automation }">
                <AutomationRow
                  :automation="automation"
                  @open="handleOpenAutomation"
                  @toggle="handleToggleAutomation"
                  @delete="handleDeleteAutomation"
                />
              </template>
            </draggable>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import draggable from 'vuedraggable';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import AutomationRow from '@/components/automations/AutomationRow.vue';
  import AutomationGroupModal from '@/components/automations/AutomationGroupModal.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import { useAutomationStore } from '@/store/automationStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';

  const sortByOrder = (items) =>
    [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  export default {
    name: 'AutomationsView',

    components: {
      PageHeader,
      AutomationRow,
      Btn,
      BaseIcon,
      EmptyState,
      draggable,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const automationStore = useAutomationStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const router = useRouter();
      const isCreating = ref(false);
      const boardGroups = ref([]);
      const ungroupedItems = ref([]);
      const groupExpanded = ref({});
      const syncingBoard = ref(false);

      const automations = computed(() => automationStore.automations);
      const groups = computed(() => automationStore.groups);

      const rebuildBoard = () => {
        syncingBoard.value = true;
        const sortedGroups = sortByOrder(groups.value || []);
        boardGroups.value = sortedGroups.map((group) => ({
          ...group,
          items: sortByOrder(
            automations.value.filter((automation) => automation.group_id === group.id)
          ),
        }));
        ungroupedItems.value = sortByOrder(
          automations.value.filter((automation) => !automation.group_id)
        );
        sortedGroups.forEach((group) => {
          if (groupExpanded.value[group.id] === undefined) {
            groupExpanded.value[group.id] = true;
          }
        });
        syncingBoard.value = false;
      };

      watch([automations, groups], rebuildBoard, { immediate: true, deep: true });

      const toggleGroup = (groupId) => {
        groupExpanded.value[groupId] = groupExpanded.value[groupId] === false;
      };

      const buildReorderPayload = () => ({
        groups: boardGroups.value.map((group, index) => ({
          id: group.id,
          sort_order: index,
        })),
        automations: [
          ...boardGroups.value.flatMap((group) =>
            group.items.map((automation, index) => ({
              id: automation.id,
              group_id: group.id,
              sort_order: index,
            }))
          ),
          ...ungroupedItems.value.map((automation, index) => ({
            id: automation.id,
            group_id: null,
            sort_order: index,
          })),
        ],
      });

      const persistOrder = async () => {
        if (syncingBoard.value) return;
        try {
          await automationStore.reorder(buildReorderPayload());
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('automations_page.reorder_failed'),
          });
          rebuildBoard();
        }
      };

      const createNewAutomation = async (groupId = null) => {
        isCreating.value = true;
        try {
          const newAutomation = await automationStore.createAutomation(groupId);
          if (newAutomation.status === 201) {
            router.push(`/automations/${newAutomation.data.data.id}`);
          }
        } catch (error) {
          console.error('Failed to create automation:', error);
        } finally {
          isCreating.value = false;
        }
      };

      const groupErrorMessage = (error) => {
        if (error?.response?.data?.error === 'group_name_taken') {
          return $t('automations_page.group_name_taken');
        }
        return $t('automations_page.group_save_failed');
      };

      const addGroup = () => {
        const modalId = uiStore.addModal(AutomationGroupModal, {
          title: $t('automations_page.group_add'),
          group: null,
          onSubmit: async ({ name }) => {
            try {
              await automationStore.createGroup(name);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('automations_page.group_created'),
              });
            } catch (error) {
              toastStore.addToast({ type: 'error', message: groupErrorMessage(error) });
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const editGroup = (group) => {
        const modalId = uiStore.addModal(AutomationGroupModal, {
          title: $t('automations_page.group_edit'),
          group,
          onSubmit: async ({ name }) => {
            try {
              await automationStore.updateGroup(group.id, { name });
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('automations_page.group_updated'),
              });
            } catch (error) {
              toastStore.addToast({ type: 'error', message: groupErrorMessage(error) });
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const confirmDeleteGroup = (group) => {
        const modalId = uiStore.addModal(false, {
          title: $t('automations_page.group_delete_title'),
          description: $t('automations_page.group_delete_description', { name: group.name }),
          submitText: $t('automations_page.delete'),
          cancelText: 'Annuleren',
          status: 'danger',
          onSubmit: async () => {
            try {
              await automationStore.deleteGroup(group.id);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('automations_page.group_deleted'),
              });
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: $t('automations_page.group_delete_failed'),
              });
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const handleOpenAutomation = (automation) => {
        router.push(`/automations/${automation.id}`);
      };

      const handleToggleAutomation = async (automation) => {
        const targetStatus = automation.status === 'active' ? 'inactive' : 'active';
        try {
          await automationStore.toggleAutomation(automation.id, targetStatus);
          toastStore.addToast({
            type: 'success',
            message:
              targetStatus === 'active'
                ? $t('automations_page.toggled_on')
                : $t('automations_page.toggled_off'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('automations_page.toggle_failed'),
          });
        }
      };

      const handleDeleteAutomation = (automation) => {
        const modalId = uiStore.addModal(false, {
          title: $t('automations_page.delete_title'),
          description: $t('automations_page.delete_description', { name: automation.name }),
          submitText: $t('automations_page.delete'),
          cancelText: 'Annuleren',
          status: 'danger',
          onSubmit: async () => {
            try {
              await automationStore.deleteAutomation(automation.id);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('automations_page.deleted'),
              });
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: $t('automations_page.delete_failed'),
              });
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      onMounted(() => {
        automationStore.fetchAutomations().catch((error) => {
          console.error('Failed to fetch automations:', error);
        });
      });

      return {
        automations,
        groups,
        boardGroups,
        ungroupedItems,
        groupExpanded,
        isCreating,
        toggleGroup,
        persistOrder,
        createNewAutomation,
        addGroup,
        editGroup,
        confirmDeleteGroup,
        handleOpenAutomation,
        handleToggleAutomation,
        handleDeleteAutomation,
      };
    },
  };
</script>

<style scoped lang="scss">
  .automations-board {
    --automation-row-grid: 24px minmax(0, 1fr) 88px 56px 56px 56px 88px 72px;
    --automation-row-gap: 0.75rem;
    --automation-row-pad-x: 0.75rem;

    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__header {
      display: grid;
      grid-template-columns: var(--automation-row-grid);
      gap: var(--automation-row-gap);
      padding: 0 calc(var(--automation-row-pad-x) + 1px) 0.5rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    &__col--num,
    &__col--status,
    &__col--actions {
      text-align: right;
    }
  }

  .automations-board__groups {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .automation-group {
    border: 1px solid var(--slate-200);
    border-radius: 12px;
    background: var(--slate-50);
    overflow: hidden;

    &--ghost {
      opacity: 0.5;
    }

    &--ungrouped {
      background: #fff;
    }

    &__header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      background: #fff;
      border-bottom: 1px solid var(--slate-100);
    }

    &__toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;
      min-width: 0;
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
      text-align: left;

      h3 {
        margin: 0;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--slate-900);
      }

      .icon.is-collapsed {
        transform: rotate(-90deg);
      }

      &--static {
        cursor: default;
      }
    }

    &__count {
      font-size: 0.75rem;
      color: var(--slate-500);
      background: var(--slate-100);
      border-radius: 999px;
      padding: 0.125rem 0.5rem;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.125rem;
    }

    &__body {
      padding: 0.25rem 0;
      background: #fff;
    }

    &__list {
      display: flex;
      flex-direction: column;
      min-height: 8px;

      &--empty {
        min-height: 48px;
      }
    }

    &__empty {
      margin: 0.5rem var(--automation-row-pad-x, 0.75rem) 0.75rem;
      width: calc(100% - 2 * var(--automation-row-pad-x, 0.75rem));
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      padding: 1.25rem;
      border: 1px dashed var(--slate-200);
      border-radius: 8px;
      background: #fff;
      color: var(--slate-500);
      font-size: 0.875rem;
      cursor: pointer;

      @include hover {
        border-color: var(--brand-400);
        color: var(--brand-600);
      }
    }
  }

  .group-drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--slate-400);
    background: none;
    border: 0;
    cursor: grab;
    padding: 0.25rem;
  }

  :deep(.automation-row--ghost) {
    opacity: 0.45;
  }
</style>
